import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Calendar heatmap (5 weeks × 7 days = 35 cells) for zero-sales streak alerts.
 *
 * Reads:
 *   context.streak_days  : string[] (ISO dates that are part of the streak)
 *   context.daily        : [{date, value}, ...] (last ~30+ days of sales)
 *   context.window_days  : optional number, defaults to 35
 */
export default function ZeroStreakCalendar({ context }) {
    const { t } = useTranslation();
    const cells = useMemo(() => {
        const streak = new Set((context?.streak_days || []).map(d => String(d).slice(0, 10)));
        const dailyMap = new Map();
        for (const d of (context?.daily || [])) {
            const k = String(d.date).slice(0, 10);
            dailyMap.set(k, Number(d.value) || 0);
        }
        const days = context?.window_days || 35;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const out = [];
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            let cls = 'muted';
            if (streak.has(key)) cls = 'streak';
            else if (dailyMap.has(key)) cls = dailyMap.get(key) > 0 ? 'sales' : 'zero';
            out.push({ key, day: d.getDate(), cls });
        }
        return out;
    }, [context]);

    return (
        <div>
            <div className="v3-anomalies-calendar">
                {cells.map(c => (
                    <div
                        key={c.key}
                        className={`v3-anomalies-cal-cell ${c.cls}`}
                        title={`${c.key} — ${t(c.cls)}`}
                    >{c.day}</div>
                ))}
            </div>
            <div className="v3-anomalies-cal-legend">
                <span><span className="swatch" style={{ background: 'rgba(239,68,68,0.6)' }} />{t('streak')}</span>
                <span><span className="swatch" style={{ background: 'rgba(245,158,11,0.4)' }} />{t('zero')}</span>
                <span><span className="swatch" style={{ background: 'rgba(6,182,212,0.25)' }} />{t('sales')}</span>
                <span><span className="swatch" style={{ background: 'rgba(255,255,255,0.06)' }} />{t('no data')}</span>
            </div>
        </div>
    );
}
